import { debug, EventTarget, JsonAsset, sp, __private } from 'cc';
import IPreloader from './IPreloader';
import BundleAssetProvider, { TaskNamingHelper } from './BundleAssetProvider';
import LoadingTask from './Toolkit/Helper/Loading/LoadingTask/LoadingTask';
import { LoadingType } from './Config/AssetLoadingConfigType';
import { exportJsonFile } from './Toolkit/Utils/Utility';
import FrameActionExecutor from './Toolkit/Utils/FrameActionExecutor';

type CachedSpineConfigData = {
    uuid: string;
    cacheMode: sp.AnimationCacheMode;
    record: SpinePreCacheData[];
    fps?: number;
    recordSlotOpacities?: boolean;
}[];

type SpinePreCacheData = {
    skinName: string;
    animationName: string;
};

const PRECACHE_SPINE_DATA_FILE_NAME = 'precache-spine-data';

const defaultCachePerFrame = 10;

class SpineManager implements IPreloader {
    private _cacheSpineDataProvider: BundleAssetProvider = null;
    private _loadingTask: LoadingTask = null;
    private _loadedPreCacheData: CachedSpineConfigData = null;
    private _fileName = PRECACHE_SPINE_DATA_FILE_NAME;

    constructor(
        private readonly cachePerFrame: number = defaultCachePerFrame,
        fileName = PRECACHE_SPINE_DATA_FILE_NAME,
    ) {
        this._fileName = fileName;
        // regenerate config to load baked shader data only
        this._cacheSpineDataProvider = new BundleAssetProvider({
            bundleName: 'resources',
            uniqueID: 'spine',
            path: {
                json: {
                    [this._fileName]: this._fileName,
                },
            },
        });
    }

    public get skeletonDataMap() {
        return sp.Skeleton.SkeletonDataMap;
    }

    public get sharedCacheInstance() {
        return sp.Skeleton.sharedCacheInstance;
    }

    public get skeletonGlobalEventTarget() {
        return sp.Skeleton.EventTarget;
    }

    public preload() {
        if (!this._loadingTask) {
            this._loadingTask = new LoadingTask(
                TaskNamingHelper.concatTaskName({
                    loadingType: LoadingType.PRELOAD,
                    providerName: this._cacheSpineDataProvider.name,
                    configName: this._cacheSpineDataProvider.assetLoadingConfigs[0].name,
                    assetName: this._fileName,
                }),
                this._cacheSpineDataProvider
                    .load(this._fileName, JsonAsset)
                    .then((precachedData) => {
                        if (!sp.Skeleton.EventTarget) {
                            sp.Skeleton.EventTarget = new EventTarget();
                        }
                        sp.Skeleton.EventTarget.on(sp.Skeleton.EventType.SKELETON_DATA_LOADED, (data: sp.SkeletonData) => {
                            const targetRecord = this._loadedPreCacheData.find((record) => record.uuid === data._uuid);
                            if (targetRecord) {
                                if (targetRecord.fps !== undefined) {
                                    data.fps = targetRecord.fps;
                                }
                                if (targetRecord.recordSlotOpacities !== undefined) {
                                    data.recordSlotOpacities = targetRecord.recordSlotOpacities;
                                }
                                this._precacheSpine(data._uuid, targetRecord.cacheMode, targetRecord.record);
                            }
                        });
                        const data = precachedData.json as CachedSpineConfigData;
                        this._loadedPreCacheData = data;
                        this._updateSkeletonDataAdditionConfig(data);
                        return this.precompileWithData(data);
                    })
                    .catch(() => {
                        // allow empty precached data
                    }),
            );
        }
        return this._loadingTask;
    }

    public recordSpineCacheData(): void {
        const record: CachedSpineConfigData = [];

        // @ts-ignore
        Object.entries(sp.Skeleton.sharedCacheInstance._skeletonCache).forEach(([uuid, data]) => {
            const cacheList: SpinePreCacheData[] = [];
            Object.entries(data.animationsCache).forEach(([key]) => {
                const strArray = key.split(':');
                cacheList.push({ skinName: strArray[0], animationName: strArray[1] });
            });
            record.push({
                uuid,
                cacheMode: sp.AnimationCacheMode.SHARED_CACHE,
                record: cacheList,
            });
        });

        // export baked shader data
        exportJsonFile(JSON.stringify(record), this._fileName);
    }

    public precompileWithData(preCachedSpineData: CachedSpineConfigData) {
        return this._precacheSpines(preCachedSpineData);
    }

    private _updateSkeletonDataAdditionConfig(spines: CachedSpineConfigData) {
        for (let i = 0; i < spines.length; i++) {
            const spine = spines[i];
            if (spine.fps) {
                const skeletonData = this.skeletonDataMap.get(spine.uuid);
                if (skeletonData) {
                    if (skeletonData.fps !== undefined) {
                        skeletonData.fps = spine.fps;
                    }
                    if (skeletonData.recordSlotOpacities !== undefined) {
                        skeletonData.recordSlotOpacities = spine.recordSlotOpacities;
                    }
                }
            }
        }
    }

    private async _precacheSpines(spines: CachedSpineConfigData) {
        for (let i = 0; i < spines.length; i++) {
            const spine = spines[i];
            await this._precacheSpine(spine.uuid, spine.cacheMode, spine.record);
        }
    }

    private async _precacheSpine(uuid: string, cacheMode: sp.AnimationCacheMode, records: SpinePreCacheData[]) {
        // TODO: Support PRIVATE_CACHE
        if (cacheMode !== sp.AnimationCacheMode.SHARED_CACHE) {
            return;
        }
        const skeletonData = this.skeletonDataMap.get(uuid);
        if (!skeletonData) {
            return;
        }
        for (let i = 0; i < records.length; i++) {
            const record = records[i];
            const { animationName, skinName } = record;
            const skeletonRuntimeData = skeletonData.getRuntimeData();
            const animationData = skeletonRuntimeData.animations.find((animation) => animation.name === animationName);
            if (!animationData) {
                continue;
            }

            const maxFrame = Math.max(1, Math.ceil(animationData.duration * 60)); // for spine default FPS=60

            sp.Skeleton.sharedCacheInstance.getSkeletonCache(uuid, skeletonRuntimeData);
            let cache = sp.Skeleton.sharedCacheInstance.getAnimationCache(uuid, skinName, animationName);
            if (!cache) {
                cache = sp.Skeleton.sharedCacheInstance.initAnimationCache(uuid, skinName, animationName);
            }
            const key = `Pre-caching spine animation: ${uuid} - ${skinName} - ${animationName}, maxFrame: ${maxFrame}`;
            debug(`${key} start`);
            await this._precacheAnimation(cache, maxFrame);
            debug(`${key} done`);
        }
    }

    private _precacheAnimation(animationCache: __private._cocos_spine_skeleton_cache__AnimationCache, maxFrame: number) {
        let index = 0;
        const executor = new FrameActionExecutor(
            () => {
                index++;
                animationCache.updateToFrame(index >= maxFrame ? undefined : index);
            },
            {
                timesPerFrame: this.cachePerFrame,
                totalTimes: maxFrame,
                dynamic: true,
            },
        );
        const promise = executor.start();
        return promise;
    }
}

export default SpineManager;
